/**
 * SHEIN 已审核商品「部分编辑」请求体（partialEdit）。
 *
 * 官方原则：
 * - 必填仅 spu_name；不传/null=保留原值，传值=更新，传""=清空
 * - 改 object[] 须提交该字段更新后的完整列表
 * - 已有 SKU：不可传 cost_info / stock_info_list / price_info_list；sale_attribute_list 编辑不可改
 * - 不使用 publishOrEdit 的 edit_type
 */
import { buildIpeEditFormSnapshot, buildIpeListPreview, buildSheinPublishPayload } from '@/shein-platform/utils/buildSheinPublishPayload';

/** 已存在 SKU 在 partialEdit 中禁止携带的字段 */
const EXISTING_SKU_OMIT_KEYS = new Set([
  'cost_info',
  'stock_info_list',
  'price_info_list',
  'sale_attribute_list',
]);

function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return String(a) === String(b);
}

function cloneJson(v) {
  return v == null ? v : JSON.parse(JSON.stringify(v));
}

/** 用发布记录摘要里的平台 SKC/SKU 编码回填表单（编辑必备） */
export function enrichFormWithPlatformSkcIds(form = {}, skcItems = []) {
  if (!form || !Array.isArray(skcItems) || skcItems.length === 0) {
    return form;
  }
  const next = cloneJson(form) || {};
  let skcList = Array.isArray(next.skcList) ? next.skcList : [];
  // 同步商品本地 stub 常无 skcList：用摘要 skcItems 先搭可编辑骨架
  if (skcList.length === 0) {
    skcList = skcItems.map((item, index) => {
      const merchantSkus = Array.isArray(item?.merchantSkus) ? item.merchantSkus : [];
      const imageUrl = String(item?.imageUrl || '').trim();
      return {
        id: `skc-from-record-${index}`,
        colorAttributeId: '',
        colorValueId: '',
        colorLabel: String(item?.colorLabel || '').trim(),
        colorIsCustom: Boolean(String(item?.colorLabel || '').trim()),
        colorExtraValue: String(item?.colorLabel || '').trim(),
        supplierCode: '',
        skcName: String(item?.skcName || '').trim(),
        mainImage: imageUrl ? { sheinUrl: imageUrl, previewUrl: imageUrl, url: imageUrl } : null,
        showroomSlots: {},
        detailImages: [],
        squareImage: null,
        colorBlockImage: null,
        skuRows: merchantSkus.length
          ? merchantSkus.map((sku) => {
            const supplierSku = String(sku?.supplierSku || '').trim();
            return {
              sizeAttributeId: '',
              sizeValueId: '',
              sizeLabel: String(sku?.sizeLabel || '').trim(),
              supplierSku,
              originalSupplierSku: supplierSku,
              skuCode: String(sku?.platformSku || sku?.skuCode || '').trim(),
              price: '',
              specialPrice: '',
              currency: '',
              stock: '',
              weight: '',
              length: '',
              width: '',
              height: '',
              quantity: '',
              quantityUnit: '1',
              packageType: '',
              mallState: 1,
              skuImage: null,
            };
          })
          : [{
            sizeAttributeId: '',
            sizeValueId: '',
            sizeLabel: '',
            supplierSku: '',
            originalSupplierSku: '',
            skuCode: '',
            price: '',
            specialPrice: '',
            currency: '',
            stock: '',
            weight: '',
            length: '',
            width: '',
            height: '',
            quantity: '',
            quantityUnit: '1',
            packageType: '',
            mallState: 1,
            skuImage: null,
          }],
      };
    });
    next.skcList = skcList;
    return next;
  }
  const used = new Set();

  const matchItem = (skc, index) => {
    const color = String(skc?.colorExtraValue || skc?.colorLabel || '').trim().toLowerCase();
    const supplier = String(skc?.supplierCode || '').trim().toLowerCase();
    const byName = skcItems.find((item, i) => {
      if (used.has(i)) return false;
      const name = String(item?.skcName || '').trim();
      return name && String(skc?.skcName || '').trim() === name;
    });
    if (byName) {
      used.add(skcItems.indexOf(byName));
      return byName;
    }
    const byColor = skcItems.find((item, i) => {
      if (used.has(i)) return false;
      const itemColor = String(item?.colorLabel || '').trim().toLowerCase();
      return color && itemColor && color === itemColor;
    });
    if (byColor) {
      used.add(skcItems.indexOf(byColor));
      return byColor;
    }
    if (index < skcItems.length && !used.has(index)) {
      used.add(index);
      return skcItems[index];
    }
    if (supplier) {
      const bySupplier = skcItems.find((item, i) => {
        if (used.has(i)) return false;
        const skus = item?.merchantSkus || [];
        return skus.some((s) => String(s?.supplierSku || '').trim().toLowerCase() === supplier);
      });
      if (bySupplier) {
        used.add(skcItems.indexOf(bySupplier));
        return bySupplier;
      }
    }
    return null;
  };

  next.skcList = skcList.map((skc, index) => {
    const hit = matchItem(skc, index);
    if (!hit) return skc;
    const merchantSkus = Array.isArray(hit.merchantSkus) ? hit.merchantSkus : [];
    const skuUsed = new Set();
    const skuRows = (skc.skuRows || []).map((sku, skuIndex) => {
      const supplierSku = String(sku?.supplierSku || '').trim().toLowerCase();
      const sizeLabel = String(sku?.sizeLabel || '').trim().toLowerCase();
      let skuHit = null;
      if (supplierSku) {
        skuHit = merchantSkus.find((row, i) => {
          if (skuUsed.has(i)) return false;
          return String(row?.supplierSku || '').trim().toLowerCase() === supplierSku;
        });
      }
      if (!skuHit && sizeLabel) {
        skuHit = merchantSkus.find((row, i) => {
          if (skuUsed.has(i)) return false;
          return String(row?.sizeLabel || '').trim().toLowerCase() === sizeLabel;
        });
      }
      if (!skuHit && skuIndex < merchantSkus.length && !skuUsed.has(skuIndex)) {
        skuHit = merchantSkus[skuIndex];
      }
      if (skuHit) {
        skuUsed.add(merchantSkus.indexOf(skuHit));
      }
      const skuCode = String(sku?.skuCode || skuHit?.skuCode || skuHit?.platformSku || '').trim();
      const originalSupplierSku = String(
        sku?.originalSupplierSku
        || skuHit?.supplierSku
        || sku?.supplierSku
        || '',
      ).trim();
      return {
        ...sku,
        ...(skuCode ? { skuCode } : {}),
        ...(originalSupplierSku && !sku?.originalSupplierSku
          ? { originalSupplierSku }
          : {}),
      };
    });
    return {
      ...skc,
      skcName: String(skc?.skcName || hit?.skcName || '').trim() || skc.skcName,
      skuRows,
    };
  });
  return next;
}

function diffSkuRow(current, baseline) {
  if (!baseline) {
    // 新增 SKU：完整字段（含 cost_info / stock），不要带 sku_code
    const row = cloneJson(current);
    if (row && row.sku_code) delete row.sku_code;
    return row;
  }
  const out = {};
  const code = current?.sku_code || baseline?.sku_code;
  if (code) out.sku_code = code;
  // 文档：编辑已有 SKU 需 spu_name + skc_name + sku_code + supplier_sku
  const supplierSku = String(current?.supplier_sku || baseline?.supplier_sku || '').trim();
  if (supplierSku) out.supplier_sku = supplierSku;
  const supplierChanged = String(current?.supplier_sku || '').trim()
    !== String(baseline?.supplier_sku || '').trim();
  Object.keys(current || {}).forEach((key) => {
    if (key === 'sku_code' || key === 'supplier_sku') return;
    // 已审 SKU：价/库存/售价/销售属性列表不可经 partialEdit 更新
    if (EXISTING_SKU_OMIT_KEYS.has(key)) return;
    if (!deepEqual(current[key], baseline[key])) {
      out[key] = cloneJson(current[key]);
    }
  });
  // 仅有标识、无实质变更 → 不提交；商家 SKU 改码算实质变更
  const meaningful = Object.keys(out).filter((k) => k !== 'sku_code' && k !== 'supplier_sku');
  if (!meaningful.length && !supplierChanged) return null;
  if (code && !out.sku_code) out.sku_code = code;
  if (supplierSku && !out.supplier_sku) out.supplier_sku = supplierSku;
  return out;
}

function diffSkcRow(current, baseline) {
  if (!baseline) {
    // 新增 SKC：完整信息，不要带 skc_name
    const row = cloneJson(current);
    if (row && row.skc_name) delete row.skc_name;
    if (Array.isArray(row?.sku_list)) {
      row.sku_list = row.sku_list.map((sku) => {
        const next = cloneJson(sku);
        if (next?.sku_code) delete next.sku_code;
        return next;
      });
    }
    return row;
  }
  const out = {};
  const skcName = current?.skc_name || baseline?.skc_name;
  if (skcName) out.skc_name = skcName;

  // partialEdit：只传有变更的 SKC 字段；不强制附带未改的 sale_attribute / image_info
  Object.keys(current || {}).forEach((key) => {
    if (key === 'skc_name' || key === 'sku_list') return;
    if (!deepEqual(current[key], baseline[key])) {
      out[key] = cloneJson(current[key]);
    }
  });

  const curSkus = Array.isArray(current?.sku_list) ? current.sku_list : [];
  const baseSkus = Array.isArray(baseline?.sku_list) ? baseline.sku_list : [];
  const used = new Set();
  const skuList = [];
  curSkus.forEach((sku, index) => {
    let base = null;
    const code = String(sku?.sku_code || '').trim();
    const supplier = String(sku?.supplier_sku || '').trim().toLowerCase();
    if (code) {
      base = baseSkus.find((row, i) => !used.has(i) && String(row?.sku_code || '').trim() === code);
    }
    if (!base && supplier) {
      base = baseSkus.find((row, i) => !used.has(i)
        && String(row?.supplier_sku || '').trim().toLowerCase() === supplier);
    }
    if (!base && index < baseSkus.length && !used.has(index)) {
      base = baseSkus[index];
    }
    if (base) used.add(baseSkus.indexOf(base));
    const diffed = diffSkuRow(sku, base);
    if (diffed) skuList.push(diffed);
  });

  // 文档：某 SKU 增改 SKU 图时，同 SKC 下其它 SKU 也需一并提交 SKU 图
  const imageTouched = skuList.some((row) => row?.image_info != null);
  if (imageTouched) {
    const byCode = new Map(
      skuList
        .filter((row) => String(row?.sku_code || '').trim())
        .map((row) => [String(row.sku_code).trim(), row]),
    );
    curSkus.forEach((sku) => {
      if (!sku?.image_info) return;
      const code = String(sku?.sku_code || '').trim();
      const supplier = String(sku?.supplier_sku || '').trim();
      if (code && byCode.has(code)) {
        const hit = byCode.get(code);
        if (!hit.image_info) hit.image_info = cloneJson(sku.image_info);
        if (supplier && !hit.supplier_sku) hit.supplier_sku = supplier;
        return;
      }
      if (code || supplier) {
        const row = {
          ...(code ? { sku_code: code } : {}),
          ...(supplier ? { supplier_sku: supplier } : {}),
          image_info: cloneJson(sku.image_info),
        };
        skuList.push(row);
        if (code) byCode.set(code, row);
      }
    });
  }

  if (skuList.length) out.sku_list = skuList;

  if (skcName && Object.keys(out).length === 1) {
    return null;
  }
  if (!Object.keys(out).length) return null;
  if (skcName && !out.skc_name) out.skc_name = skcName;
  return out;
}

function findBaselineSkc(current, baselineList, used) {
  const name = String(current?.skc_name || '').trim();
  const supplier = String(current?.supplier_code || '').trim().toLowerCase();
  if (name) {
    const hit = baselineList.find((row, i) => !used.has(i) && String(row?.skc_name || '').trim() === name);
    if (hit) {
      used.add(baselineList.indexOf(hit));
      return hit;
    }
  }
  if (supplier) {
    const hit = baselineList.find((row, i) => !used.has(i)
      && String(row?.supplier_code || '').trim().toLowerCase() === supplier);
    if (hit) {
      used.add(baselineList.indexOf(hit));
      return hit;
    }
  }
  return null;
}

/**
 * 复色等「只在已发布 SPU 下新增 SKC」的 partialEdit 请求体。
 *
 * 官方场景 2：可以只传新 SKC 的信息，提供 "spu_name + 新 skc 的完整 skc_list" 即可；
 * 不传/null = 平台保留原值（标题、描述、SPU 属性、站点、已有 SKC 都不用回传），
 * 因此这里只组新 SKC，避免把未改动内容（尤其未转存的视频/图片）重新提交触发校验。
 *
 * @param {object} form 至少含 spuName，且 skcList 只放本次新增的颜色
 * @param {{ attrTpl?: object, sizeAttrs?: object[], skuPkgCaps?: object, previewForm?: object }} [options]
 *   previewForm：站内回填/列表预览用的完整表单（含已有 SKC、印花图），只写入 _ipe_* 私有字段，不发平台
 */
export function buildSheinRecolorPayload(form, {
  sizeAttrs = [],
  skuPkgCaps,
  attrTpl = null,
  previewForm = null,
  standardRaw = null,
} = {}) {
  if (!form?.spuName) {
    throw new Error('复色缺少平台 SPU，无法提交改品');
  }
  const newSkcs = (Array.isArray(form.skcList) ? form.skcList : []).filter(Boolean);
  if (!newSkcs.length) {
    throw new Error('复色没有可新增的颜色');
  }
  const built = buildSheinPublishPayload(
    // 未改动内容不参与组包：视频等只做校验的字段直接不传
    { ...form, skcList: newSkcs, sheinVideos: [], productVideos: [] },
    {
      isEdit: true,
      sizeAttrs,
      skuPkgCaps,
      shopType: form?.shopType,
      siteList: form?.siteList,
      siteCurrencyMap: form?.siteCurrencyMap,
      attrTpl,
      standardRaw,
    },
  );
  const payload = {
    spu_name: form.spuName,
    skc_list: Array.isArray(built.skc_list) ? built.skc_list : [],
  };
  if (!payload.skc_list.length) {
    throw new Error('复色没有可提交的颜色规格');
  }
  // 站内私有字段（后端打平台前会剥掉）：
  // - _ipe_edit_form：完整商品（含原 SKC / 标题 / 印花），供草稿回填与发布记录标题继承
  // - _ipe_list_preview：必须与 skc_list 一一对齐（只含本次新增颜色），
  //   否则后端按 index 合并时会把原色名（如 White）盖到新色主图上
  const snapshotSource = previewForm && typeof previewForm === 'object' ? previewForm : form;
  payload._ipe_edit_form = buildIpeEditFormSnapshot(snapshotSource);
  payload._ipe_list_preview = buildIpeListPreview({
    ...snapshotSource,
    // 标题沿用原 SPU；skc 只留本次复色的颜色，与 payload.skc_list 同序
    title: String(snapshotSource.title || form.title || '').trim(),
    skcList: newSkcs,
  });
  return payload;
}

/**
 * 相对 baseline 做 partialEdit 增量体。
 * 仅 spu_name 必填；类目/标题等仅在变更时带上（不传则平台保留原值）。
 */
export function buildSheinEditPayload(form, {
  baselineForm = null,
  sizeAttrs = [],
  skuPkgCaps,
  attrTpl = null,
  standardRaw = null,
} = {}) {
  if (!form?.spuName) {
    throw new Error('编辑商品缺少平台 SPU，无法提交改品');
  }
  const current = buildSheinPublishPayload(form, {
    isEdit: true,
    sizeAttrs,
    skuPkgCaps,
    shopType: form?.shopType,
    siteList: form?.siteList,
    siteCurrencyMap: form?.siteCurrencyMap,
    attrTpl,
    standardRaw,
  });
  current.spu_name = form.spuName;

  const categoryId = resolvePositiveId(form.categoryId, current.category_id);
  const productTypeId = resolvePositiveId(form.productTypeId, current.product_type_id);

  if (!baselineForm) {
    // 无 baseline：仍走 partialEdit 语义，去掉 publishOrEdit 专用字段
    const fallback = cloneJson(current);
    delete fallback.edit_type;
    delete fallback.site_list;
    fallback.spu_name = form.spuName;
    if (categoryId) fallback.category_id = categoryId;
    if (productTypeId) fallback.product_type_id = productTypeId;
    // 无 baseline 时无法判断新旧 SKU，保守去掉已有编码 SKU 上的价/库存
    if (Array.isArray(fallback.skc_list)) {
      fallback.skc_list = fallback.skc_list.map((skc) => {
        if (!skc?.skc_name || !Array.isArray(skc.sku_list)) return skc;
        return {
          ...skc,
          sku_list: skc.sku_list.map((sku) => {
            if (!sku?.sku_code) return sku;
            const next = { ...sku };
            EXISTING_SKU_OMIT_KEYS.forEach((key) => { delete next[key]; });
            return next;
          }),
        };
      });
    }
    return fallback;
  }

  const baseline = buildSheinPublishPayload(baselineForm, {
    isEdit: true,
    sizeAttrs,
    skuPkgCaps,
    shopType: form?.shopType || baselineForm?.shopType,
    siteList: form?.siteList || baselineForm?.siteList,
    siteCurrencyMap: form?.siteCurrencyMap || baselineForm?.siteCurrencyMap,
    attrTpl,
    standardRaw,
  });
  const out = {
    spu_name: form.spuName,
  };

  // 类目仅变更时传入（文档为可选；不传保留）
  if (categoryId && categoryId !== resolvePositiveId(baselineForm.categoryId, baseline.category_id)) {
    out.category_id = categoryId;
  }
  if (productTypeId
    && productTypeId !== resolvePositiveId(baselineForm.productTypeId, baseline.product_type_id)) {
    out.product_type_id = productTypeId;
  }

  const alwaysDiffKeys = [
    'brand_code',
    'suit_flag',
    'supplier_code',
    'multi_language_name_list',
    'multi_language_desc_list',
    'product_attribute_list',
    'size_attribute_list',
    'ip_character_list',
    'fill_configuration_info',
  ];
  alwaysDiffKeys.forEach((key) => {
    if (!(key in current)) return;
    if (!deepEqual(current[key], baseline[key])) {
      out[key] = cloneJson(current[key]);
    }
  });

  const curSkc = Array.isArray(current.skc_list) ? current.skc_list : [];
  const baseSkc = Array.isArray(baseline.skc_list) ? baseline.skc_list : [];
  const used = new Set();
  const skcList = [];
  curSkc.forEach((skc, index) => {
    let base = findBaselineSkc(skc, baseSkc, used);
    if (!base && index < baseSkc.length && !used.has(index) && !skc?.skc_name) {
      base = baseSkc[index];
      used.add(index);
    }
    const diffed = diffSkcRow(skc, base);
    if (diffed) skcList.push(diffed);
  });
  if (skcList.length) out.skc_list = skcList;

  if (current._ipe_list_preview) {
    out._ipe_list_preview = current._ipe_list_preview;
  }
  if (current._ipe_edit_form) {
    out._ipe_edit_form = current._ipe_edit_form;
  }

  // 除 spu_name / 站内私有字段外无任何变更
  const meaningfulKeys = Object.keys(out).filter(
    (k) => k !== 'spu_name' && k !== '_ipe_list_preview' && k !== '_ipe_edit_form',
  );
  if (!meaningfulKeys.length) {
    throw new Error('没有可提交的变更。已审核 SKU 的售价/成本价请在提交时走专用改价接口；其余字段未改动。');
  }
  return out;
}

function resolvePositiveId(...values) {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** 编辑提交前：相对 baseline 已存在的规格必须带平台编码，新增规格可无编码 */
export function validateEditPlatformIds(form = {}, baselineForm = null) {
  const baselineSkc = Array.isArray(baselineForm?.skcList) ? baselineForm.skcList : [];
  const missing = [];
  (form.skcList || []).forEach((skc, skcIndex) => {
    const hasColor = skc?.colorValueId || String(skc?.colorExtraValue || skc?.colorLabel || '').trim();
    if (!hasColor) return;
    const color = String(skc?.colorExtraValue || skc?.colorLabel || '').trim().toLowerCase();
    const baseSkc = baselineSkc.find((row) => {
      if (String(row?.skcName || '').trim() && String(row?.skcName || '').trim() === String(skc?.skcName || '').trim()) {
        return true;
      }
      const baseColor = String(row?.colorExtraValue || row?.colorLabel || '').trim().toLowerCase();
      return color && baseColor && color === baseColor;
    });
    const isExistingSkc = Boolean(baseSkc) || Boolean(String(skc?.skcName || '').trim());
    if (isExistingSkc && !String(skc?.skcName || '').trim()) {
      missing.push(`SKC#${skcIndex + 1} 缺少平台 skc_name`);
    }
    (skc.skuRows || []).forEach((sku, skuIndex) => {
      if (!sku?.sizeValueId && !sku?.supplierSku) return;
      const supplierSku = String(sku?.supplierSku || '').trim();
      const supplierKey = supplierSku.toLowerCase();
      const baseSku = (baseSkc?.skuRows || []).find((row) => {
        if (String(row?.skuCode || '').trim() && String(row?.skuCode || '').trim() === String(sku?.skuCode || '').trim()) {
          return true;
        }
        return supplierKey && String(row?.supplierSku || '').trim().toLowerCase() === supplierKey;
      });
      const isExistingSku = Boolean(baseSku) || Boolean(String(sku?.skuCode || '').trim());
      if (isExistingSku && !String(sku?.skuCode || '').trim()) {
        missing.push(`SKU#${skcIndex + 1}-${skuIndex + 1} 缺少平台 sku_code`);
      }
      // partialEdit 定位已有 SKU：sku_code + supplier_sku
      if (isExistingSku && !supplierSku) {
        missing.push(`SKU#${skcIndex + 1}-${skuIndex + 1} 缺少商家 SKU（supplier_sku）`);
      }
    });
  });
  return {
    ok: missing.length === 0,
    message: missing.length
      ? `本地缺少平台规格编码，无法按「部分编辑」提交（${missing.slice(0, 3).join('；')}${missing.length > 3 ? '…' : ''}）。请先在产品信息页拉取 SHEIN 后台数据后再编辑。`
      : '',
  };
}
